import React, { useEffect, useState } from "react";
import "../../index.css";
import { FaSliders } from "react-icons/fa6";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { RiHotelLine } from "react-icons/ri";
import { getLocations, getRestaurantsOrHotels } from "../../api/user";
import { useQuery } from "@tanstack/react-query";
import Location from "../Home/Location";
import { MdStar, MdStarHalf, MdStarOutline } from "react-icons/md";
import UserPagination from "../../components/common/UserPagination";
import { useTranslation } from "../../hooks/useTranslation";
import { useSettings } from "../../context/SettingsContext";
import { HotelListingLoading } from "../../helper/lorder";

const HotelBooking = () => {
  const { t } = useTranslation();
  const navigate = useNavigate("");
  const locationRouter = useLocation();
  const { settings } = useSettings();
  const showHotels = settings?.showHotels;

  const initialFilters = {
    location: "",
    minPrice: "",
    maxPrice: "",
    ratings: "",
    amenities: "",
  };
  const [filters, setFilters] = useState({ ...initialFilters });
  const [appliedFilters, setAppliedFilters] = useState({ ...initialFilters });
  const [userLocation, setUserLocation] = useState({
    latitude: null,
    longitude: null,
  });
  const [page, setPage] = useState(1);
  const limit = 6;

  // Redirect to home if hotel bookings are not enabled
  useEffect(() => {
    if (showHotels === false) {
      navigate("/");
    }
  }, [showHotels, navigate]);

  const { data: locationsData } = useQuery({
    queryKey: ["locations"],
    queryFn: () => getLocations({ hotels: true }),
    keepPreviousData: true,
    enabled: showHotels !== false, // Only fetch if hotels are enabled
  });
  const locations = locationsData?.data?.data || [];

  // If QR params are present, redirect to existing hotel menu UI with guest state
  useEffect(() => {
    const params = new URLSearchParams(locationRouter.search);
    const hotelId = params.get("hotel") || params.get("hotelId");
    const roomId = params.get("room") || params.get("roomId");
    if (hotelId && roomId) {
      navigate(`/hotel-menu`, { state: { hotelId, roomId, guest: true } });
    }
  }, [locationRouter.search, navigate]);

  const {
    data: searchedData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["hotels-restaurants", appliedFilters, userLocation, page, limit],
    queryFn: () =>
      getRestaurantsOrHotels({
        hotels: true,
        rating: appliedFilters.ratings,
        address: appliedFilters.location,
        features:
          appliedFilters.amenities === "All amenities"
            ? undefined
            : appliedFilters.amenities,
        min: appliedFilters.minPrice,
        max: appliedFilters.maxPrice,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        page,
        limit,
      }),
    keepPreviousData: true,
    enabled: showHotels !== false, // Only fetch if hotels are enabled
  });

  const data = searchedData?.data?.data;
  const pagination = searchedData?.data?.pagination;

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Location error:", error);
        }
      );
    } else {
      console.error(t("hotelbookingfilter").geolocationNotSupported);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    const formattedValue =
      name === "amenities"
        ? value
          .split("+")
          .map((v) => v.trim())
          .join(",")
        : value;

    setFilters((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ ...initialFilters });
    setAppliedFilters({ ...initialFilters });
    setPage(1);
  };

  const amenityTranslations = t("hotelbookingfilter").amenityOptions || {};
  
  const featureOptions = [
    { value: "wifi", label: amenityTranslations.wifi || "Free Wi-Fi" },
    { value: "Parking Available", label: amenityTranslations.parkingAvailable || "Parking Available" },
    { value: "Outdoor Seating", label: amenityTranslations.outdoorSeating || "Outdoor Seating" },
    { value: "Live Music", label: amenityTranslations.liveMusic || "Live Music" },
    { value: "Pet Friendly", label: amenityTranslations.petFriendly || "Pet Friendly" },
    { value: "Kid Friendly", label: amenityTranslations.kidFriendly || "Kid Friendly" },
    // { value: "vegan_options", label: "Vegan Options" },
    { value: "Wheelchair Accessible", label: amenityTranslations.wheelchairAccessible || "Wheelchair Accessible" },
    { value: "Accepts Reservations", label: amenityTranslations.acceptsReservations || "Accepts Reservations" },
    { value: "Takeaway Available", label: amenityTranslations.takeawayAvailable || "Takeaway Available" },
    // { value: "delivery", label: "Home Delivery" },
    // { value: "buffet", label: "Buffet Service" },
    // { value: "bar", label: "Bar Available" },
    // { value: "private_dining", label: "Private Dining" },
    { value: "Smoking Area", label: amenityTranslations.smokingArea || "Smoking Area" },
    { value: "Swimming Pool", label: amenityTranslations.swimmingPool || "Swimming Pool" },
    { value: "Fitness Center", label: amenityTranslations.fitnessCenter || "Fitness Center" },
    { value: "Spa", label: amenityTranslations.spa || "Spa" },
    { value: "Air Conditioning", label: amenityTranslations.airConditioning || "Air Conditioning" },
  ];

  // Don't render anything if hotel bookings are disabled
  if (showHotels === false) {
    return null;
  }

  return (
    <div>
      <div className="w-full md:h-[503px] h-[300px] object-cover">
        <Location data={locations}></Location>
      </div>

      {/* Fiter */}
      <div className="container">
        <div className="bg-white rounded-[10px] lg:p-[40px] p-5 relative top-[-50px] filter-shadow z-10">
          <div className="flex items-center gap-[20px] lg:mb-[25px] mb-4 justify-between">
            <h2 className="lg:text-[30px] sm:text-xl text-base font-medium">
              {t("hotelbookingfilter").checkAvailableHotels}
            </h2>
            <Link className="flex items-center gap-[9px] justify-end sm:text-xl text-sm font-medium">
              <FaSliders />
              {t("hotelbookingfilter").filter}
            </Link>
          </div>
          <div className="grid lg:grid-cols-5 sm:grid-cols-3 grid-cols-1 gap-5 items-center">
            <input
              type="text"
              name="location"
              placeholder={t("hotelbookingfilter").location}
              value={filters.location}
              onChange={handleChange}
              className="border border-[#E0E2E7] lg:px-[24px] px-4 py-[15px] text-sm focus:outline-none rounded-[5px]"
            />
            <input
              type="number"
              name="minPrice"
              placeholder={t("hotelbookingfilter").price}
              value={filters.minPrice}
              onChange={handleChange}
              className="border border-[#E0E2E7] lg:px-[24px] px-4 py-[15px] text-sm focus:outline-none rounded-[5px]"
            />
            <input
              type="number"
              name="ratings"
              placeholder={t("hotelbookingfilter").ratings}
              value={filters.ratings}
              onChange={handleChange}
              className="border border-[#E0E2E7] lg:px-[24px] px-4 py-[15px] text-sm focus:outline-none rounded-[5px]"
            />
            {/* <input
              type="text"
              name="amenities"
              placeholder="Amenities"
              value={filters.amenities}
              onChange={handleChange}
              className="border border-[#E0E2E7] lg:px-[24px] px-4 py-[15px] text-sm focus:outline-none rounded-[5px]"
            /> */}

            <select
              name="amenities"
              value={filters.amenities}
              onChange={handleChange}
              className="border border-[#E0E2E7] lg:px-[24px] px-4 py-[15px] text-sm focus:outline-none rounded-[5px]"
            >
              <option value="">{t("hotelbookingfilter").allAmenities}</option>
              {featureOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              className="md:w-auto w-full rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-bold 2xl:p-[14px] 2xl:px-[41px] p-3 green-shadow hover:bg-[#209b25]"
              onClick={handleSearch}
            >
              {t("hotelbookingfilter").search}
            </button>
          </div>
        </div>

 {isLoading?<HotelListingLoading/>:<div className="lg:pb-[100px] md:pb-[70px] pb-[30px]">
          {/* Fiter results */}
          <div className="grid grid-cols-1 lg:grid-cols-3 sm:grid-cols-2 gap-[21px] items-center ">
            {/* =========1st col item ========= */}

            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : isError ? (
              <p className="text-red-500 text-center mt-6">{error.message}</p>
            ) : data?.length > 0 ? (
              data?.map((item) => {
                return (
                  <div
                    key={item?._id}
                    className="relative rounded-[10px] bg-white border border-[#D9D9D9] cursor-pointer"
                    onClick={() => {
                      navigate(`/hoteldetail`, {
                        state: { hotelId: item?._id },
                      });
                    }}
                  >

                    {item?.isSponsored && (
                      <span className="absolute top-2 left-2 bg-[#4CAF50] text-white text-xs px-3 py-1 rounded-full">
                        {t("home").discoverSection.sponsored}
                      </span>
                    )}
                    <div>
                      <img
                        src={`${item?.images?.[0]}`}
                        alt="hotel"
                        className="w-full h-[236px] object-cover rounded-t-[9px]"
                      />
                    </div>
                    <div className="pt-[14px] pb-[23px] pr-[17px] pl-[10px]">
                      <h2 className="text-lg font-semibold leading-[30px] mb-[5px] text-[#18181B]">
                        {item?.name}
                      </h2>
                      <div className="flex items-center gap-[10px] justify-between flex-wrap">
                        <span className="text-base leading-[26px] text-[#18181B]">
                          {item?.address}
                        </span>
                        <div className="flex items-center justify-end">
                          {Array(Math.floor(item?.rating))
                            .fill()
                            .map((_, index) => (
                              <MdStar
                                key={`full-${index}`}
                                size={21}
                                className="text-[#FF9D00]"
                              />
                            ))}

                          {item?.rating % 1 !== 0 && (
                            <MdStarHalf size={21} className="text-[#FF9D00]" />
                          )}

                          {Array(5 - Math.ceil(item?.rating))
                            .fill()
                            .map((_, index) => (
                              <MdStarOutline
                                key={`empty-${index}`}
                                size={21}
                                className="text-[#FF9D00]"
                              />
                            ))}
                        </div>
                      </div>
                      <div className="flex gap-[10px] items-center mt-[11px]">
                        <RiHotelLine size={24} className="text-[#7B7B7B]" />
                        <span className="text-[15px] leading-[30px] text-[#18181B]">
                          5 stars - 8.2/10 Very Good (1,008)
                        </span>
                      </div>
                      <p className="text-[15px] text-[#4CAF50] semibold mt-[10px]">
                        {item?.distance
                          ? `${item?.distance} ${t("home").searchFilter.distanceText
                          }`
                          : null}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full flex items-center justify-center py-16">
                <div className="max-w-xl w-full bg-white border border-dashed border-[#D9D9D9] rounded-[12px] px-6 py-10 text-center shadow-sm">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-[#EEF7EF] text-[#4CAF50] mb-6">
                    <RiHotelLine size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#18181B] mb-3">
                    {t("hotelbookingfilter").noHotelsFound}
                  </h3>
                  <p className="text-sm md:text-base text-[#6B7280] leading-6">
                    {t("home").searchFilter.noResultsDescription}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3 justify-center">
                    <button
                      type="button"
                      onClick={handleClearFilters}
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#4CAF50] text-[#4CAF50] rounded-full text-sm font-semibold hover:bg-[#EEF7EF] transition-colors"
                    >
                      {t("restaurant").search.clearFilter || "Clear Filters"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {pagination?.pages > 1 && (
            <UserPagination
              currentPage={page}
              setCurrentPage={setPage}
              totalPages={pagination.pages}
            />
          )}
        </div>}
        
      </div>
    </div>
  );
};

export default HotelBooking;
